
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';
import logo from '../../assets/images/logo.png';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Check if current language is RTL
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      // Close mobile menu when scrolling
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  // Recalculate navLinks when language changes
  const navLinks = [
    { name: t('home', { ns: 'common' }), path: '/' },
    { name: t('product', { ns: 'common' }), path: '/product' },
    { name: t('applications', { ns: 'common' }), path: '/applications' },
    { name: t('aboutUs', { ns: 'common' }), path: '/about' },
    { name: t('contact', { ns: 'common' }), path: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled && !isMobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      style={{ height: '80px' }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4 h-full flex items-center">
        {/* Logo */}
        <Link to="/" className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'order-2' : 'order-1'}`}>
          {location.pathname === '/' ? (
            <h1>
              <img
                src={logo}
                alt="Dynamix Steel Fiber Logo"
                className={`h-10 w-auto transition-all duration-300 ${isScrolled && !isMobileMenuOpen ? '' : 'brightness-0 invert'
                  }`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </h1>
          ) : (
            <img
              src={logo}
              alt="Dynamix Steel Fiber Logo"
              className={`h-10 w-auto transition-all duration-300 ${isScrolled && !isMobileMenuOpen ? '' : 'brightness-0 invert'
                }`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}
        </Link>

        {/* Spacer for mobile - pushes hamburger to the correct side */}
        <div className={`flex-1 md:hidden ${isRTL ? 'order-1' : 'order-2'}`}></div>

        {/* Desktop Navigation */}
        <div className={`hidden md:flex items-center gap-6 ${isRTL ? 'mr-auto order-1' : 'ml-auto order-2'}`}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-medium whitespace-nowrap transition-colors duration-300 hover:text-cyan-500 ${location.pathname === link.path
                ? 'text-cyan-500'
                : isScrolled
                  ? 'text-gray-700'
                  : 'text-white'
                }`}
              onClick={() => {
                // Scroll to top if already on this page
                if (location.pathname === link.path) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              {link.name}
            </Link>
          ))}
          <LanguageSwitcher />
        </div>

        {/* Mobile Language Switcher */}
        <div className={`md:hidden flex items-center ${isRTL ? 'ml-2 order-3' : 'mr-2 order-3'}`}>
          <LanguageSwitcher />
        </div>

        {/* Mobile Menu Button - positioned correctly for RTL */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors duration-300 flex-shrink-0 ${isRTL ? 'ml-0 order-4' : 'mr-0 order-4'
            } ${isScrolled && !isMobileMenuOpen ? 'text-gray-900' : 'text-white'}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-lg md:hidden pt-24"
          >
            {/* Close Button - Fixed at Top Right Corner */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.05 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed top-6 right-6 text-white hover:text-cyan-500 transition-colors duration-300 z-50"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-4xl"></i>
            </motion.button>

            <div className="container mx-auto px-4 h-full flex flex-col items-center justify-start gap-8">
              {/* Navigation Links */}
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`text-2xl font-bold transition-colors duration-300 hover:text-cyan-500 ${location.pathname === link.path ? 'text-cyan-500' : 'text-white'
                      }`}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      if (location.pathname === link.path) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
